// Hand-synced duplicate of NobleAdmin/mcp/src/repos/personalizationSpec.ts.
// The MCP copy is the source; edit there first, then paste the body here.
// Only this header differs.

// Per-field personalization scoring for launch readiness.
//
// The app personalizes by truthiness only. ThePath
// screens/Onboarding-Intro/components/questionnaire/components/resultsFlow/
// BeforeAfterSlide.tsx does:
//
//   const personalize = (authored, fallback) => authored ? ... : fallback;
//
// So an absent or empty field always renders the generic translated string,
// identical across every community on the platform. Presence checking catches
// nearly every leak. Exact default-string matching is the secondary net for
// the case where a generator wrote the default string in verbatim.
//
// This module is pure. It takes already-fetched documents and returns counts
// plus a leak list. No Firestore access.

type AnyRecord = Record<string, any>;

export type GroupKey =
  | "questionnaire"
  | "permissionConsents"
  | "appCopy"
  | "paywall"
  | "community"
  | "author";

export type Leak = {
  group: GroupKey;
  field: string;
  status: "generic-default";
  reason: "unset" | "matches-default";
  required: boolean;
  rendersAs?: string;
  fix: string;
};

export type GroupResult = {
  authored: number;
  total: number;
  leaks: Leak[];
};

export type PersonalizationResult = {
  authored: number;
  total: number;
  percent: number;
  leaks: Leak[];
  byGroup: Record<GroupKey, GroupResult>;
};

export const GROUP_FIX: Record<GroupKey, string> = {
  questionnaire: "save_questionnaire",
  permissionConsents: "save_questionnaire",
  appCopy: "update_community_app_copy",
  paywall: "upsert_paywall",
  community: "update_community",
  author: "upsert_author",
};

export const GROUP_LABEL: Record<GroupKey, string> = {
  questionnaire: "questionnaire",
  permissionConsents: "permission consent copy",
  appCopy: "app copy",
  paywall: "paywall",
  community: "community",
  author: "author",
};

// ---------------------------------------------------------------------------
// Generic-default copy table.
//
// Hand-kept mirror of two ThePath files, the same way every other cross-repo
// contract in this codebase is kept in sync by hand:
//   - assets/translations/en.json: onboarding.results.*, login.*,
//     home.checkin.*, learning.*, learning.prompts.*, and the
//     experience-level labels.
//   - hooks/checkInNotificationCopy.ts: NOTIFICATION_COPY.
//
// It is a safety net, not the primary mechanism. Presence checking already
// catches nearly every leak, so a stale entry here degrades gracefully: the
// field falls back to presence scoring only.
// ---------------------------------------------------------------------------

const BEFORE_AFTER_DEFAULTS: Record<string, string> = {
  badge: "Transformation",
  headline: "See the difference",
  subheadline: "Your journey starts here",
  beforeTitle: "Where you are now",
  afterTitle: "Where you'll be",
  timeframe: "IN 30 DAYS",
  ctaLabel: "Start my path",
  coachNote: "I'll walk this path with you, step by step.",
};

const COMMIT_DEFAULTS: Record<string, string> = {
  greeting: "Let's make a contract",
  primaryCta: "I commit to myself",
};

const ENTRY_CTA_DEFAULTS: Record<string, string> = {
  getStartedLabel: "Get Started",
  loginLabel: "Log In",
};

const CHECK_IN_CARD_DEFAULTS: Record<string, string> = {
  title: "Daily check-in",
  prompt: "Take a moment to check in",
  cta: "Check in",
  checkedInTitle: "Checked in today",
};

const CREATE_LEARNING_DEFAULTS: Record<string, string> = {
  title: "Create Your Learning Journey",
  subtitle: "Personalized learning, designed for you",
  topicLabel: "What do you wish to learn?",
  topicDescription: "Describe the topic or subject you'd like to explore",
  topicPlaceholder: "Enter the topic or subject area...",
  goalLabel: "What's your goal?",
  goalDescription: "What should you be able to do or understand by the end?",
  goalPlaceholder: "Describe your desired outcome...",
  difficultyLabel: "Difficulty Level",
  difficultyDescription: "Choose your current knowledge level",
  contextLabel: "What's your context / why do you care?",
  contextDescription: "I'm learning this because…",
  contextPlaceholder: "Share your motivation or context...",
  submitLabel: "Start Your Learning Journey",
};

// A stored prompt list counts as generic only when EVERY item is one of these.
const LEARNING_PROMPT_DEFAULTS = [
  "What do you want to master?",
  "Ready to learn something new?",
  "What skill will you build today?",
  "Start your learning journey",
  "What's sparking your curiosity?",
  "What would you love to dive into?",
  "What do you want to understand better?",
  "Pick a subject and let's begin",
  "What will you grow in today?",
  "What's on your mind to learn?",
  "What's worth learning this week?",
  "What's your next learning goal?",
];

const LEVEL_NAME_DEFAULTS: Record<string, string> = {
  apprentice: "Apprentice",
  journeyman: "Journeyman",
  adept: "Adept",
  expert: "Expert",
  virtuoso: "Virtuoso",
  sage: "Sage",
  master: "Master",
};

export const LEVEL_KEYS = Object.keys(LEVEL_NAME_DEFAULTS);

// Only the first title of each default list is known here. A stored titles
// array counts as generic when it CONTAINS its anchor.
const CHECK_IN_NOTIFICATION_ANCHORS = {
  morning: "Set your intention for today",
  evening: "Reflect on your day",
};

// ---------------------------------------------------------------------------
// Spec types
// ---------------------------------------------------------------------------

type FieldKind = "string" | "array" | "object";

type FieldSpec = {
  path: string;
  kind: FieldKind;
  required: boolean;
  // Known generic default strings for this field.
  defaults?: string[];
  // How an array of strings is compared against defaults. "every" means the
  // whole list is the stock list; "contains" means one stock entry is enough.
  defaultMatch?: "every" | "contains";
};

const str = (path: string, required = true, ...defaults: string[]): FieldSpec => ({
  path,
  kind: "string",
  required,
  ...(defaults.length ? { defaults } : {}),
});

const arr = (
  path: string,
  required = true,
  defaults?: string[],
  defaultMatch: "every" | "contains" = "every"
): FieldSpec => ({
  path,
  kind: "array",
  required,
  ...(defaults ? { defaults, defaultMatch } : {}),
});

const obj = (path: string, required = true): FieldSpec => ({
  path,
  kind: "object",
  required,
});

// ---------------------------------------------------------------------------
// Static specs
// ---------------------------------------------------------------------------

const QUESTIONNAIRE_FIELDS: FieldSpec[] = [
  arr("questions"),
  str("ai_prompt"),
  str("loading.title"),
  str("loading.progress_label"),
  arr("loading.alerts"),
  arr("loading.steps"),
  arr("interstitials"),
  ...Object.entries(BEFORE_AFTER_DEFAULTS).map(([key, value]) =>
    str(`beforeAfter.${key}`, true, value)
  ),
  arr("beforeAfter.before"),
  arr("beforeAfter.after"),
  str("commit.greeting", true, COMMIT_DEFAULTS.greeting),
  arr("commit.commitments"),
  str("commit.subcopy"),
  str("commit.primaryCta", true, COMMIT_DEFAULTS.primaryCta),
  str("ready.headline"),
  arr("ready.bullets"),
  str("ready.primaryCta"),
  arr("reviews"),
];

const NOTIFICATION_CONSENT_FIELDS: FieldSpec[] = [
  str("permissionConsents.notification.title"),
  str("permissionConsents.notification.subtitle"),
  str("permissionConsents.notification.previewLabel"),
  str("permissionConsents.notification.scheduleIntro"),
  str("permissionConsents.notification.cta"),
  str("permissionConsents.notification.morning.label"),
  str("permissionConsents.notification.morning.message"),
  str("permissionConsents.notification.evening.label"),
  str("permissionConsents.notification.evening.message"),
];

const consentSectionFields = (section: "health" | "focus"): FieldSpec[] => [
  str(`permissionConsents.${section}.title`),
  str(`permissionConsents.${section}.subtitle`),
  arr(`permissionConsents.${section}.bullets`),
  str(`permissionConsents.${section}.previewLabel`),
  arr(`permissionConsents.${section}.previewItems`),
  str(`permissionConsents.${section}.previewOutcome`),
  str(`permissionConsents.${section}.cta`),
];

const APP_COPY_FIELDS: FieldSpec[] = [
  ...Object.entries(ENTRY_CTA_DEFAULTS).map(([key, value]) =>
    str(`entryCta.${key}`, true, value)
  ),
  ...Object.entries(CREATE_LEARNING_DEFAULTS).map(([key, value]) =>
    str(`createLearning.${key}`, true, value)
  ),
  ...Object.entries(CHECK_IN_CARD_DEFAULTS).map(([key, value]) =>
    str(`checkInCard.${key}`, true, value)
  ),
  arr(
    "checkInNotifications.morning.titles",
    true,
    [CHECK_IN_NOTIFICATION_ANCHORS.morning],
    "contains"
  ),
  arr(
    "checkInNotifications.evening.titles",
    true,
    [CHECK_IN_NOTIFICATION_ANCHORS.evening],
    "contains"
  ),
  arr("learningPrompts.items", true, LEARNING_PROMPT_DEFAULTS, "every"),
  ...LEVEL_KEYS.map((key) =>
    str(`levelNames.${key}`, true, LEVEL_NAME_DEFAULTS[key])
  ),
  ...LEVEL_KEYS.map((key) => str(`levelIcons.${key}`, false)),
  obj("brain", false),
];

const PAYWALL_FIELDS: FieldSpec[] = [
  str("title"),
  str("subtitle"),
  str("buttonText"),
  str("featuresTitle"),
  arr("features"),
  str("testimonialsTitle"),
  arr("testimonials"),
  str("howTitle"),
  arr("howSteps"),
  arr("faqs"),
  arr("products"),
  arr("plans"),
  str("motivationalPrompt", false),
  str("videoURL", false),
];

// Group D of the plan: the community-root fields the coarse checks miss.
// title, description, colors, fontTheme, stylePack, entryValueProps, coverURL,
// imagesURL and insightScore are already gated by their own checks.
// visualStyle and writingStyle are scored on the resolved value, see
// resolveCommunityStyles: the root fields are deprecated and the canonical
// home is the mediaConfig doc.
const COMMUNITY_FIELDS: FieldSpec[] = [
  str("shortDescription"),
  arr("tags"),
  str("visualStyle"),
  str("writingStyle"),
  str("logoURL", false),
  obj("winbackNotifications", false),
];

const AUTHOR_FIELDS: FieldSpec[] = [
  str("fullName"),
  str("description"),
  str("aiGuidelines"),
  str("profilePhotoURL"),
];

// ---------------------------------------------------------------------------
// Emptiness and default matching
// ---------------------------------------------------------------------------

const normalize = (value: string) => value.trim().toLowerCase();

// By design `false` and `0` read as unset. Every scored field is authored copy
// or an asset reference, so a falsy scalar there means the coach wrote nothing
// and the app renders its generic default.
function isNonEmptyValue(value: unknown): boolean {
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.some((item) => isNonEmptyValue(item));
  if (value && typeof value === "object") {
    // A sibling i18n key is metadata, never a field.
    return Object.entries(value as AnyRecord).some(
      ([key, item]) => key !== "i18n" && isNonEmptyValue(item)
    );
  }
  return false;
}

function readPath(doc: AnyRecord, path: string): unknown {
  return path.split(".").reduce<any>((node, key) => {
    if (node == null || typeof node !== "object") return undefined;
    return node[key];
  }, doc);
}

// A document read helper may hand back { exists: false, note }. That means
// every field is unset, so score it as an empty document.
function asDocument(doc: AnyRecord | null | undefined): AnyRecord {
  if (!doc || typeof doc !== "object") return {};
  if ((doc as AnyRecord).exists === false) return {};
  return doc;
}

function matchesDefault(value: unknown, spec: FieldSpec): boolean {
  if (!spec.defaults || !spec.defaults.length) return false;
  const wanted = new Set(spec.defaults.map(normalize));
  if (spec.kind === "string") {
    return typeof value === "string" && wanted.has(normalize(value));
  }
  if (spec.kind === "array" && Array.isArray(value)) {
    const entries = value.filter(
      (item): item is string => typeof item === "string" && item.trim().length > 0
    );
    if (!entries.length) return false;
    if (spec.defaultMatch === "contains") {
      return entries.some((item) => wanted.has(normalize(item)));
    }
    return entries.length === value.length &&
      entries.every((item) => wanted.has(normalize(item)));
  }
  return false;
}

function evaluateField(
  doc: AnyRecord,
  spec: FieldSpec,
  group: GroupKey,
  fieldPrefix = ""
): Leak | null {
  const value = readPath(doc, spec.path);
  const field = `${fieldPrefix}${spec.path}`;
  const rendersAs = spec.defaults?.length ? spec.defaults[0] : undefined;
  if (!isNonEmptyValue(value)) {
    return {
      group,
      field,
      status: "generic-default",
      reason: "unset",
      required: spec.required,
      ...(rendersAs ? { rendersAs } : {}),
      fix: GROUP_FIX[group],
    };
  }
  if (matchesDefault(value, spec)) {
    return {
      group,
      field,
      status: "generic-default",
      reason: "matches-default",
      required: spec.required,
      ...(rendersAs ? { rendersAs } : {}),
      fix: GROUP_FIX[group],
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Procedural expansions
// ---------------------------------------------------------------------------

// An interstitial background can be a single uri or a list of them, under four
// different keys. Score the first key the card actually carries, so one authored
// form never reads as a leak on the other three. Nothing set falls back to the
// single-uri key, which names the field in the leak.
const BACKGROUND_KEYS = [
  "backgroundImageUri",
  "backgroundUri",
  "backgroundImageUris",
  "backgroundUris",
] as const;

function backgroundField(entry: AnyRecord, prefix: string): FieldSpec {
  const key =
    BACKGROUND_KEYS.find((candidate) => isNonEmptyValue(entry?.[candidate])) ||
    BACKGROUND_KEYS[0];
  const isList = key.endsWith("Uris");
  return isList ? arr(`${prefix}${key}`, false) : str(`${prefix}${key}`, false);
}

// Interstitial sub-fields depend on the card's own type.
function interstitialFields(entry: AnyRecord, index: number): FieldSpec[] {
  const prefix = `interstitials[${index}].`;
  const fields: FieldSpec[] = [
    str(`${prefix}headline`),
    str(`${prefix}body`),
    str(`${prefix}ctaLabel`),
    str(`${prefix}caption`, false),
    // The app reads backgroundImageUri || backgroundUri, and the array forms
    // backgroundImageUris || backgroundUris. Score whichever one is set.
    backgroundField(entry, prefix),
  ];
  const type = String(entry?.type || "").trim().toLowerCase();
  if (type === "testimonial") {
    fields.push(str(`${prefix}review`), str(`${prefix}reviewer`));
  }
  if (type === "quote") fields.push(str(`${prefix}author`));
  if (type === "credibility") fields.push(arr(`${prefix}badges`));
  return fields;
}

// Interstitial paths carry a [n] segment, so they need their own reader.
function readInterstitialPath(doc: AnyRecord, path: string): unknown {
  const match = /^interstitials\[(\d+)\]\.(.+)$/.exec(path);
  if (!match) return readPath(doc, path);
  const list = Array.isArray(doc.interstitials) ? doc.interstitials : [];
  const entry = list[Number(match[1])];
  return entry ? readPath(entry as AnyRecord, match[2]) : undefined;
}

// Health and focus consent copy is scored only when that section exists on the
// document or its key is listed in permissionConsents.order.
function consentSectionsInUse(questionnaire: AnyRecord): Array<"health" | "focus"> {
  const consents = (readPath(questionnaire, "permissionConsents") || {}) as AnyRecord;
  const order = Array.isArray(consents.order)
    ? consents.order.map((key: unknown) => String(key || "").trim())
    : [];
  return (["health", "focus"] as const).filter(
    (key) => consents[key] !== undefined || order.includes(key)
  );
}

// The app never reads visualStyle or writingStyle. They feed the Media Studio,
// ad and landing generators, and their canonical home is
// Community/{id}/CommunityMedia/mediaConfig; the root fields are deprecated
// (src/types/Community.ts). NobleAdmin src/database/community/mediaConfig.ts
// getCommunityMediaConfig is the resolver: mediaConfig first, then the root
// doc, where the older slideVisualStyle key also counts. For visual style the
// generators prefer visualStyleProfile (description, then tags) over the flat
// string (buildVisualStylePromptBlock). Mirror that here so a community that
// migrated off the root fields never reads as a permanent leak. The leak field
// names stay "visualStyle" and "writingStyle" and the fix stays
// update_community, which the resolver still honors through the root fallback.
function firstAuthored(...candidates: unknown[]): unknown {
  return candidates.find((value) => isNonEmptyValue(value));
}

function resolveVisualStyle(mediaConfig: AnyRecord, community: AnyRecord): unknown {
  const profile = (mediaConfig.visualStyleProfile || {}) as AnyRecord;
  const tags = Array.isArray(profile.tags)
    ? profile.tags.filter(
        (tag: unknown): tag is string =>
          typeof tag === "string" && tag.trim().length > 0
      )
    : [];
  return firstAuthored(
    profile.description,
    tags.length ? tags.join(", ") : undefined,
    mediaConfig.visualStyle,
    community.slideVisualStyle,
    community.visualStyle
  );
}

function resolveCommunityStyles(
  community: AnyRecord,
  mediaConfig: AnyRecord
): AnyRecord {
  return {
    ...community,
    writingStyle: firstAuthored(mediaConfig.writingStyle, community.writingStyle),
    visualStyle: resolveVisualStyle(mediaConfig, community),
  };
}

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

const emptyGroup = (): GroupResult => ({ authored: 0, total: 0, leaks: [] });

export function evaluatePersonalization(docs: {
  questionnaire?: AnyRecord | null;
  appCopy?: AnyRecord | null;
  paywall?: AnyRecord | null;
  community?: AnyRecord | null;
  // Community/{id}/CommunityMedia/mediaConfig. Optional: without it the root
  // style fields are scored alone, the pre-migration behavior.
  mediaConfig?: AnyRecord | null;
  authors?: AnyRecord[] | null;
}): PersonalizationResult {
  // The app resolves the results-flow groups from either shape: ThePath
  // utils/onboardingConfig.ts reads resultsFlow.beforeAfter || beforeAfter, the
  // same for commit and ready, and resultsFlow.aiInsightPrompt || ai_prompt.
  // Resolve once here so every group below reads one shape.
  const rawQuestionnaire = asDocument(docs.questionnaire);
  const resultsFlow = (rawQuestionnaire.resultsFlow || {}) as AnyRecord;
  const questionnaire: AnyRecord = {
    ...rawQuestionnaire,
    beforeAfter: resultsFlow.beforeAfter || rawQuestionnaire.beforeAfter,
    commit: resultsFlow.commit || rawQuestionnaire.commit,
    ready: resultsFlow.ready || rawQuestionnaire.ready,
    ai_prompt: resultsFlow.aiInsightPrompt || rawQuestionnaire.ai_prompt,
  };
  const appCopy = asDocument(docs.appCopy);
  const paywall = asDocument(docs.paywall);
  const community = resolveCommunityStyles(
    asDocument(docs.community),
    asDocument(docs.mediaConfig)
  );
  const authors = Array.isArray(docs.authors) ? docs.authors : [];

  const byGroup: Record<GroupKey, GroupResult> = {
    questionnaire: emptyGroup(),
    permissionConsents: emptyGroup(),
    appCopy: emptyGroup(),
    paywall: emptyGroup(),
    community: emptyGroup(),
    author: emptyGroup(),
  };

  const record = (group: GroupKey, leak: Leak | null) => {
    const target = byGroup[group];
    target.total += 1;
    if (leak) target.leaks.push(leak);
    else target.authored += 1;
  };

  // A. Questionnaire
  for (const spec of QUESTIONNAIRE_FIELDS) {
    record("questionnaire", evaluateField(questionnaire, spec, "questionnaire"));
  }
  const interstitials = Array.isArray(questionnaire.interstitials)
    ? (questionnaire.interstitials as AnyRecord[])
    : [];
  interstitials.forEach((entry, index) => {
    for (const spec of interstitialFields(entry || {}, index)) {
      const value = readInterstitialPath(questionnaire, spec.path);
      const leak = isNonEmptyValue(value)
        ? null
        : {
            group: "questionnaire" as const,
            field: spec.path,
            status: "generic-default" as const,
            reason: "unset" as const,
            required: spec.required,
            fix: GROUP_FIX.questionnaire,
          };
      record("questionnaire", leak);
    }
  });

  // Permission consent copy (its own readiness check).
  for (const spec of NOTIFICATION_CONSENT_FIELDS) {
    record(
      "permissionConsents",
      evaluateField(questionnaire, spec, "permissionConsents")
    );
  }
  for (const section of consentSectionsInUse(questionnaire)) {
    for (const spec of consentSectionFields(section)) {
      record(
        "permissionConsents",
        evaluateField(questionnaire, spec, "permissionConsents")
      );
    }
  }

  // B. App copy
  for (const spec of APP_COPY_FIELDS) {
    record("appCopy", evaluateField(appCopy, spec, "appCopy"));
  }

  // E. Paywall
  for (const spec of PAYWALL_FIELDS) {
    record("paywall", evaluateField(paywall, spec, "paywall"));
  }

  // D. Community root (visualStyle and writingStyle already resolved against
  // the mediaConfig doc above).
  for (const spec of COMMUNITY_FIELDS) {
    record("community", evaluateField(community, spec, "community"));
  }

  // F. Authors
  authors.forEach((author, index) => {
    const doc = author || {};
    for (const spec of AUTHOR_FIELDS) {
      const leak = evaluateField(doc, spec, "author");
      record(
        "author",
        leak ? { ...leak, field: `authors[${index}].${spec.path}` } : null
      );
    }
    // Voice and agent are one optional capability: either id counts.
    const hasVoice =
      isNonEmptyValue(doc.voiceId) || isNonEmptyValue(doc.agentId);
    record(
      "author",
      hasVoice
        ? null
        : {
            group: "author",
            field: `authors[${index}].voiceId|agentId`,
            status: "generic-default",
            reason: "unset",
            required: false,
            fix: GROUP_FIX.author,
          }
    );
  });

  const groups = Object.values(byGroup);
  const total = groups.reduce((sum, g) => sum + g.total, 0);
  const authored = groups.reduce((sum, g) => sum + g.authored, 0);
  const leaks = (Object.keys(byGroup) as GroupKey[]).flatMap(
    (key) => byGroup[key].leaks
  );

  return {
    authored,
    total,
    percent: Math.round((authored / Math.max(total, 1)) * 100),
    leaks,
    byGroup,
  };
}

// Detail string for a section check: "12/18 fields authored. Generic
// defaults: a, b, +3 more."
export function describeGroup(result: GroupResult, maxNamed = 5): string {
  // No fields scored (an empty group, or a fetch that failed) says nothing
  // about personalization, so add nothing to the caller's detail string.
  if (result.total === 0) return "";
  const base = `${result.authored}/${result.total} fields authored.`;
  if (!result.leaks.length) return base;
  const names = result.leaks.map((leak) => leak.field);
  const shown = names.slice(0, maxNamed);
  const rest = names.length - shown.length;
  return `${base} Generic defaults: ${shown.join(", ")}${
    rest > 0 ? `, +${rest} more` : ""
  }.`;
}

// Only required leaks flip a section check to partial.
export function hasRequiredLeak(result: GroupResult): boolean {
  return result.leaks.some((leak) => leak.required);
}
